// Real vanilla Skyrim animation event names - verified against a working
// reference implementation (not guessed FormIDs). Triggering these locally
// via Debug.sendAnimationEvent on the player syncs to other clients
// automatically through the existing animation sync pipeline (see
// skymp5-client's sync/animation.ts + sendInputsService.ts's AnimationSource
// - no bespoke networking needed for this feature).
export interface EmoteData {
  id: string;
  label: string;
  image: string;
  // Same-family alternates (e.g. the 4 "parler en marchant" variants) -
  // hovering this slot in the wheel reveals them in a flyout (index.tsx's
  // VariantFlyout) instead of each one taking its own slot on the ring.
  // The slot's own id/image/label above are always variants[0]'s, so a
  // plain click on it still just plays an emote directly, same as any
  // ungrouped one - grouping only changes what hovering shows.
  variants?: EmoteData[];
}

// Collapses a same-family list into one wheel slot. `label` becomes the
// family name shown as the slot's tooltip/center-hint (rather than that
// first variant's own more specific label) - the variants keep their
// original, more specific labels for the flyout.
function groupFamily(label: string, variants: EmoteData[]): EmoteData {
  return { ...variants[0], label, variants };
}

// Shorter, one-shot social gestures.
export const OUTER_RING_EMOTES: EmoteData[] = [
  { id: "OffsetArmsCrossedStart", label: "Bras croisés", image: "idle-crossedarms.gif" },
  groupFamily("Pointer", [
    { id: "IdlePointFar_01", label: "Pointer au loin", image: "pointfar.gif" },
    { id: "IdlePointFar_02", label: "Pointer au loin (2)", image: "pointfar2.png" },
    { id: "IdlePointClose", label: "Pointer de près", image: "pointclose.png" },
  ]),
  { id: "PickUp", label: "Ramasser", image: "pickup.png" },
  { id: "IdleSilentBow", label: "Révérence silencieuse", image: "silentbow.gif" },
  { id: "IdleStudy", label: "Étudier", image: "study.gif" },
  { id: "IdleTake", label: "Prendre", image: "take.png" },
  { id: "IdleWave", label: "Saluer de la main", image: "wave.gif" },
  { id: "IdleSurrender", label: "Se rendre", image: "surrender.gif" },
  { id: "IdleLookFar", label: "Regarder au loin", image: "lookfar.gif" },
  { id: "IdleDrink", label: "Boire", image: "drink.gif" },
  { id: "IdleSalute", label: "Saluer", image: "salute.gif" },
  { id: "IdleExamine", label: "Examiner", image: "examine.gif" },
  { id: "IdleGive", label: "Donner", image: "give.png" },
  { id: "IdleCivilWarCheer", label: "Acclamer", image: "war-cheer.gif" },
  { id: "IdleGetAttention", label: "Attirer l'attention", image: "getattention.gif" },
  { id: "IdleMT_DoorBang", label: "Frapper à la porte", image: "knockdoor.gif" },
  { id: "IdleWriteLedgerEnter", label: "Écrire dans un registre", image: "ledger.png" },
  { id: "idleLooseSweepingStart", label: "Balayer", image: "sweeping.gif" },
  groupFamily("Applaudir", [
    { id: "IdleApplaudSarcastic", label: "Applaudir (sarcastique)", image: "applaud-sarcastic.png" },
    { id: "IdleApplaud3", label: "Applaudir chaleureusement", image: "applaud3.png" },
    { id: "IdleApplaud4", label: "Applaudir bruyamment", image: "applaud4.gif" },
    { id: "IdleApplaud5", label: "Applaudir avec enthousiasme", image: "applaud5.gif" },
  ]),
  { id: "IdleBlowHornImperial", label: "Souffler dans le cor (Légion)", image: "blowhorn-imperial.gif" },
  { id: "IdleBlowHornStormcloak", label: "Souffler dans le cor (Sombrages)", image: "blowhorn-stormcloak.gif" },
  { id: "IdleBook_Reading", label: "Lire un livre", image: "bookreading.png" },
  { id: "IdleBook_PageTurn", label: "Tourner les pages d'un livre", image: "bookpageturn.gif" },
  { id: "OffsetCarryPotStart", label: "Porter un pot", image: "carrypot.png" },
  { id: "IdleCO2Ceremony2Blessing", label: "Bénédiction cérémonielle", image: "ceremony-blessing.png" },
  { id: "IdleCO2Ceremony3Chest", label: "Ouvrir un coffre (cérémonie)", image: "ceremony-chest.png" },
  { id: "IdleCO2Ceremony1Welcome", label: "Geste de bienvenue cérémoniel", image: "ceremony-welcome.png" },
  { id: "IdleCiceroDance1", label: "Danse de Cicéron (1)", image: "cicerodance1.gif" },
  { id: "IdleCleanSword", label: "Nettoyer son épée", image: "cleansword.png" },
  { id: "IdleUncontrollableCough", label: "Tousser", image: "cough.png" },
  { id: "IdleDialogueDefensiveHandGesture", label: "Geste défensif", image: "defensivegesture.png" },
  { id: "IdleDialogueHandOnChinGesture", label: "Réfléchir, main au menton", image: "handonchin.gif" },
  { id: "OffsetCarryMQ201DrinkTray", label: "Porter un plateau de boissons", image: "drinktray.png" },
  { id: "IdleHandCut", label: "Se couper la main", image: "handcut.png" },
  { id: "IdleHoe", label: "Biner la terre", image: "hoe.gif" },
  groupFamily("Être blessé", [
    { id: "IdleInjured", label: "Être blessé", image: "injured.png" },
    { id: "IdleWounded_01", label: "Être blessé (1)", image: "wounded1.png" },
    { id: "IdleWounded_02", label: "Être blessé (2)", image: "wounded2.gif" },
    { id: "IdleWounded_03", label: "Être blessé (3)", image: "wounded3.gif" },
  ]),
  { id: "IdleLockPick", label: "Crocheter une serrure", image: "lockpick.png" },
  { id: "IdleMesmerize", label: "Envoûter", image: "mesmerize.png" },
  { id: "IdleNervous", label: "Être nerveux", image: "nervous.png" },
  { id: "IdlePickup_Ground", label: "Ramasser au sol", image: "pickup-ground.png" },
  { id: "IdlePullChain", label: "Tirer une chaîne", image: "pullchain.png" },
  { id: "LooseDialogueResponseNegative", label: "Réponse négative", image: "response-negative.png" },
  { id: "LooseDialogueResponsePositive", label: "Réponse positive", image: "response-positive.png" },
  { id: "IdleRitualStart", label: "Rituel", image: "ritual.gif" },
  groupFamily("Rituel avec un crâne", [
    { id: "IdleRitualSkull1", label: "Rituel avec un crâne (1)", image: "ritualskull1.png" },
    { id: "IdleRitualSkull2", label: "Rituel avec un crâne (2)", image: "ritualskull2.png" },
    { id: "IdleRitualSkull3", label: "Hurler au ciel (crâne)", image: "new-emote.png" },
  ]),
  { id: "IdleScrapeTreeBark", label: "Gratter l'écorce", image: "scrapebark.png" },
  { id: "IdleSell", label: "Vendre", image: "sell.png" },
  { id: "IdlestaticposeA", label: "Pose statique", image: "staticpose-a.png" },
  groupFamily("Parler en marchant", [
    { id: "IdleDialogueMovingTalkA", label: "Parler en marchant (1)", image: "talk-a.png" },
    { id: "IdleDialogueMovingTalkB", label: "Parler en marchant (2)", image: "talk-b.png" },
    { id: "IdleDialogueMovingTalkC", label: "Parler en marchant (3)", image: "talk-c.png" },
    { id: "IdleDialogueMovingTalkD", label: "Parler en marchant (4)", image: "talk-d.png" },
  ]),
  { id: "IdleMQ201ToastStart", label: "Porter un toast", image: "toast.png" },
  { id: "IdleWarmArms", label: "Se réchauffer les bras", image: "warmarms.png" },
  { id: "IdleWipeBrow", label: "S'essuyer le front", image: "wipebrow.gif" },
  // Vanilla idles added later: no animated preview yet, they share a placeholder picture
  { id: "IdleComeThisWay", label: "Faire signe de venir", image: "new-emote.png" },
  { id: "IdleDialogueWelcomeHandGesture", label: "Geste d'accueil", image: "new-emote.png" },
  { id: "IdleSnapToAttention", label: "Garde-à-vous", image: "new-emote.png" },
  { id: "IdleSleepNod", label: "Somnoler", image: "new-emote.png" },
  { id: "MT_BreathExhaleShort", label: "Souffler", image: "new-emote.png" },
  groupFamily("Spectateur", [
    { id: "SpectatorClap", label: "Applaudir (spectateur)", image: "new-emote.png" },
    { id: "SpectatorGawk", label: "Regarder, bouche bée", image: "new-emote.png" },
    { id: "SpectatorTalking", label: "Commenter en spectateur", image: "new-emote.png" },
  ]),
  groupFamily("Humeur de Cicéron", [
    { id: "IdleCiceroHappy", label: "Joie (Cicéron)", image: "new-emote.png" },
    { id: "IdleCiceroAgitated", label: "Agitation (Cicéron)", image: "new-emote.png" },
  ]),
  // From the Emote mod (Nexus 17580): its 90 animations are the game's own idles, so only the events are added
  { id: "IdleBracedPain", label: "Encaisser un coup", image: "new-emote.png" },
  { id: "IdleBook_OnePage", label: "Tourner une page", image: "new-emote.png" },
  { id: "IdleDrinkPotion", label: "Boire une potion", image: "new-emote.png" },
  { id: "IdleLightSconce", label: "Allumer une applique", image: "new-emote.png" },
  { id: "IdleGreybeardWordTeach", label: "Enseigner un mot de pouvoir", image: "new-emote.png" },
  { id: "IdleMagic_01", label: "Geste magique", image: "new-emote.png" },
  { id: "IdlePrisonerBeat", label: "Frapper un prisonnier", image: "new-emote.png" },
  { id: "IdleMQ206FelldirShowScroll", label: "Montrer un parchemin", image: "new-emote.png" },
  { id: "IdleWriteLedgerWrite", label: "Écrire dans un registre (écriture)", image: "new-emote.png" },
  { id: "IdleFeedChicken", label: "Nourrir les poules", image: "new-emote.png" },
  { id: "IdleOffsetCarryPotServe", label: "Servir avec un pot", image: "new-emote.png" },
  { id: "IdleDwemerExtractorEnter", label: "Actionner un extracteur dwemer", image: "new-emote.png" },
  groupFamily("Dévorer", [
    { id: "IdleCannibalFeedStanding", label: "Dévorer (debout)", image: "new-emote.png" },
    { id: "IdleCannibalFeedCrouching", label: "Dévorer (accroupi)", image: "new-emote.png" },
  ]),
  // From Immersive Interactions (Nexus 47670), whose events are registered by the behavior patch
  // (altherys-immersive-animations): working gestures, played by the activities too
  { id: "AO_Cut", label: "Découper à la dague", image: "new-emote.png" },
  { id: "AO_Tan", label: "Tanner une peau", image: "new-emote.png" },
  { id: "AO_PickUp", label: "Ramasser (à hauteur de main)", image: "new-emote.png" },
  { id: "AO_PickupLow", label: "Ramasser (bas)", image: "new-emote.png" },
  { id: "AO_IdleKnock", label: "Frapper à la porte (poing)", image: "new-emote.png" },
  { id: "AO_OpenDoor", label: "Ouvrir une porte (geste)", image: "new-emote.png" },
  // Carried or drunk while walking
  { id: "OffsetFlowerBasketStart", label: "Porter un panier de fleurs", image: "new-emote.png" },
  { id: "IdleMQ201Drink", label: "Boire à la coupe", image: "new-emote.png" },
  { id: "DrunkStart", label: "Marcher en titubant", image: "new-emote.png" },
  groupFamily("Gestes d'attente", [
    { id: "idle_A_scratch_back", label: "Se gratter le dos", image: "new-emote.png" },
    { id: "idle_A_swat_flies", label: "Chasser les mouches", image: "new-emote.png" },
    { id: "idle_A_shakeout", label: "Se détendre les membres", image: "new-emote.png" },
    { id: "idle_A_shoulders", label: "Rouler les épaules", image: "new-emote.png" },
  ]),
];

// Longer looping activities (sitting, playing an instrument, etc.) - these
// are the ones most worth binding "exitAnimation" to.
export const INNER_RING_EMOTES: EmoteData[] = [
  { id: "IdleSitCrossLeggedEnter", label: "S'asseoir en tailleur", image: "sitcross.gif" },
  { id: "IdleLayDownEnter", label: "S'allonger", image: "laiddown.png" },
  { id: "IdlePray", label: "Prier", image: "pray.gif" },
  { id: "IdleGrave_01", label: "Se recueillir (tombe)", image: "pray-grave.gif" },
  { id: "IdleGrave_02", label: "Pose formelle", image: "formal-stand.gif" },
  { id: "idledrumstart", label: "Jouer du tambour", image: "drum.gif" },
  { id: "idlelutestart", label: "Jouer du luth", image: "lute.gif" },
  { id: "idleflutestart", label: "Jouer de la flûte", image: "flute.gif" },
  { id: "IdleWarmHandsStanding", label: "Réchauffer ses mains", image: "warmhands.gif" },
  { id: "IdleWarmHandsCrouched", label: "Réchauffer ses mains (assis)", image: "warmhandssitting.gif" },
  { id: "idleEatingStandingStart", label: "Manger", image: "eating.gif" },
  { id: "IdleCiceroDance2", label: "Danse de Cicéron (2)", image: "cicerodance2.gif" },
  { id: "IdleCiceroDance3", label: "Danse de Cicéron (3)", image: "cicerodance3.gif" },
  { id: "IdleApplaud2", label: "Applaudir", image: "clapping.gif" },
  { id: "IdleLaugh", label: "Rire", image: "laugh.gif" },
  { id: "IdleNoteRead", label: "Lire une note", image: "note.gif" },
  { id: "IdleHandsBehindBack", label: "Mains derrière le dos", image: "handsbehindback.gif" },
  { id: "IdleBarCounterEnter", label: "S'accouder au comptoir", image: "barcounter.png" },
  { id: "idleBarDrinkingStart", label: "Boire au comptoir", image: "bardrinking.png" },
  { id: "IdleBlessingKneelEnter", label: "S'agenouiller pour une bénédiction", image: "blessingkneel.png" },
  { id: "IdleBoundKneesEnterInstant", label: "Être à genoux, mains liées", image: "boundknees.png" },
  { id: "IdleCarryBucketFillEnter", label: "Remplir un seau", image: "bucketfill.png" },
  { id: "IdleChiselKneelingEnter", label: "Ciseler à genoux", image: "chiselkneeling.png" },
  { id: "IdleChiselStandingEnter", label: "Ciseler debout", image: "chiselstanding.png" },
  { id: "IdleCoffinEnter", label: "S'allonger dans un cercueil", image: "coffin.png" },
  { id: "IdleCowerEnter", label: "Se recroqueviller", image: "cower.gif" },
  { id: "IdleExecutioneeIdle", label: "Attendre l'exécution", image: "executionee.png" },
  { id: "IdleHammerTableEnter", label: "Marteler une table", image: "hammertable.png" },
  { id: "IdleHammerWallEnter", label: "Marteler un mur", image: "hammerwall.png" },
  { id: "IdleKneelingEnter", label: "S'agenouiller", image: "kneel.gif" },
  { id: "IdleLeanTableEnter", label: "S'appuyer sur une table", image: "leantable.png" },
  { id: "IdleGreybeardMeditateEnter", label: "Méditer", image: "meditate.png" },
  { id: "IdleWallCornerPeekLEnterInstant", label: "Épier au coin (gauche)", image: "peekcorner-left.png" },
  { id: "IdleWallCornerPeekREnterInstant", label: "Épier au coin (droite)", image: "peekcorner-right.png" },
  { id: "idleRitualSpellStart", label: "Rituel de sortilège", image: "ritualspell.png" },
  { id: "IdleSearchChestEnter", label: "Fouiller un coffre", image: "searchchest.png" },
  { id: "IdleSearchTableEnter", label: "Fouiller une table", image: "searchtable.png" },
  { id: "IdleSitLedgeEnter", label: "S'asseoir sur un rebord", image: "sitledge.png" },
  { id: "IdleStonePickUpEnterInstant", label: "Ramasser une pierre", image: "stonepickup.png" },
  { id: "IdleWallLeanEnterInstant", label: "S'adosser au mur", image: "walllean.png" },
  { id: "IdleWriteTableStandingEnter", label: "Écrire debout à une table", image: "writetable.png" },
  { id: "idleDrinkingStandingStart", label: "Boire debout", image: "new-emote.png" },
  // From the Emote mod (Nexus 17580)
  { id: "IdleBoyRitual", label: "Planter une lame au sol", image: "new-emote.png" },
  { id: "IdleAstridEnter", label: "Se prélasser", image: "new-emote.png" },
  { id: "IdleCowering", label: "Trembler de peur", image: "new-emote.png" },
  { id: "IdleKneeling", label: "Fouiller un corps", image: "new-emote.png" },
  { id: "IdleWarmHands", label: "Se réchauffer les mains (2)", image: "new-emote.png" },
  { id: "IdleSearchingTable", label: "Fouiller une table (en boucle)", image: "new-emote.png" },
  { id: "IdleSearchingChest", label: "Fouiller un coffre (en boucle)", image: "new-emote.png" },
  { id: "IdleBook_Read", label: "Lire un livre, assis", image: "new-emote.png" },
  // From Immersive Interactions (Nexus 47670)
  { id: "AO_NoteStart", label: "Lire une note (en main)", image: "new-emote.png" },
];
